/**
 * DEMO-ONLY: sessionStorage keys for Presidio mock ICM workflows.
 * Removable with mock.interceptor.presidio.ts and assets/mock-data.
 */
import { notifyPresidioFavoritesLoginChange } from 'src/app/extensions/presidio-favorites/services/presidio-favorites-session';

import { clearPresidioMockBasketLines, clearPresidioMockBasketLineShipTos } from './presidio-mock-basket-state';

export const PRESIDIO_MOCK_QR_SUBMITTED_PREFIX = 'presidio-mock-qr-submitted:';

/** DEMO: My Account preferred ship-to / invoice / payment overrides. */
export const PRESIDIO_MOCK_USER_PREFS_KEY = 'presidio-mock-user-prefs';

/**
 * DEMO: which mock login succeeded last (password + MFA).
 * Drives `customers/-` / `customers/-/users/-` fixture rewrite for multi-customer demos.
 */
export const PRESIDIO_MOCK_ACTIVE_LOGIN_KEY = 'presidio-mock-active-login';

/** DEMO: warehouse customer (MVP 2.6.3) — login as this user to skip OPC payment. */
export const PRESIDIO_MOCK_WAREHOUSE_LOGIN = 'casey.ware@presidio-wh.com';
export const PRESIDIO_MOCK_WAREHOUSE_CUSTOMER_NO = 'PRESIDIO-WH-MOCK';

/** DEMO: default SMB customer (Acme / Jordan — Admin). */
export const PRESIDIO_MOCK_DEFAULT_LOGIN = 'jordan.hale@acme.com';
export const PRESIDIO_MOCK_DEFAULT_CUSTOMER_NO = 'PRESIDIO-MOCK';

/** DEMO: pure DIAS B2B role users under Acme (PRESIDIO-MOCK). */
export const PRESIDIO_MOCK_BUYER_LOGIN = 'alex.rivera@acme.com';
export const PRESIDIO_MOCK_APPROVER_LOGIN = 'morgan.chen@acme.com';
export const PRESIDIO_MOCK_ACCOUNT_OWNER_LOGIN = 'sam.patel@acme.com';
/** DEMO: standard buyer with zero affiliate assignments (no Select account / header switcher). */
export const PRESIDIO_MOCK_NO_AFFILIATE_LOGIN = 'jamie.stone@acme.com';

/** DEMO: punchout shopper (MVP 2.5.1) — transfer cart back to procurement. */
export const PRESIDIO_MOCK_PUNCHOUT_LOGIN = 'pat.punch@acme.com';
export const PRESIDIO_MOCK_PUNCHOUT_SID = 'presidio-demo-punchout-sid';
export const PRESIDIO_MOCK_PUNCHOUT_RETURN_PATH = '/punchout/demo-return';

/**
 * DEMO: Presidio sales rep (MVP 8.2.1) — affiliate multi-account switch.
 * Login picker + header switcher use `availableSoldTos` on the user fixture.
 */
export const PRESIDIO_MOCK_SALES_REP_LOGIN = 'riley.sales@presidio.com';
export const PRESIDIO_MOCK_ACTIVE_CUSTOMER_KEY = 'presidio-mock-active-customer';
export const PRESIDIO_MOCK_SALES_ACCOUNT_PICKED_KEY = 'presidio-mock-sales-account-picked';
/** In-flight login-gate picker — survives LoginPage remounts so Select account cannot open twice. */
export const PRESIDIO_MOCK_SALES_ACCOUNT_PICKING_KEY = 'presidio-mock-sales-account-picking';

/** DEMO: affiliate customer accounts available to the sales-rep demo user. */
export const PRESIDIO_MOCK_SALES_REP_ACCOUNTS: readonly { customerNo: string; companyName: string }[] = [
  { customerNo: 'PRESIDIO-MOCK', companyName: 'Acme Healthcare' },
  { customerNo: 'PRESIDIO-AFF-BETA', companyName: 'Beta Manufacturing' },
  { customerNo: 'PRESIDIO-AFF-GAMMA', companyName: 'Gamma Education' },
];

/**
 * DEMO: Presidio CSR (MVP 8.2.2) — impersonate end users; ATC allowed, checkout blocked while impersonating.
 * Separate from sales-rep affiliate switch (Riley / 8.2.1).
 */
export const PRESIDIO_MOCK_CSR_LOGIN = 'quinn.csr@presidio.com';
/** Header chrome identity while co-browsing — matches Quinn CSR fixture (not Viewing as). */
export const PRESIDIO_MOCK_CSR_DISPLAY_NAME = 'Quinn CSR';
export const PRESIDIO_MOCK_IMPERSONATED_LOGIN_KEY = 'presidio-mock-impersonated-login';
export const PRESIDIO_MOCK_IMPERSONATED_NAME_KEY = 'presidio-mock-impersonated-name';

/** DEMO: Acme end users a CSR may view-as (exclude CSR / sales / punchout / warehouse). */
export const PRESIDIO_MOCK_CSR_IMPERSONATION_TARGETS: readonly {
  login: string;
  username: string;
  firstName: string;
  lastName: string;
  displayName: string;
}[] = [
  {
    login: PRESIDIO_MOCK_DEFAULT_LOGIN,
    username: 'jordan.hale',
    firstName: 'Jordan',
    lastName: 'Hale',
    displayName: 'Jordan Hale',
  },
  {
    login: PRESIDIO_MOCK_BUYER_LOGIN,
    username: 'alex.rivera',
    firstName: 'Alex',
    lastName: 'Rivera',
    displayName: 'Alex Rivera',
  },
  {
    login: PRESIDIO_MOCK_APPROVER_LOGIN,
    username: 'morgan.chen',
    firstName: 'Morgan',
    lastName: 'Chen',
    displayName: 'Morgan Chen',
  },
  {
    login: PRESIDIO_MOCK_ACCOUNT_OWNER_LOGIN,
    username: 'sam.patel',
    firstName: 'Sam',
    lastName: 'Patel',
    displayName: 'Sam Patel',
  },
];

/** DEMO: searchable catalog size for CSR view-as (production may be thousands). */
export const PRESIDIO_MOCK_CSR_SEARCH_CATALOG_SIZE = 2500;

const PRESIDIO_MOCK_PUNCHOUT_SESSION_KEYS = [
  'punchout_SID',
  'punchout_ReturnURL',
  'punchout_BasketID',
  'punchout_HookURL',
] as const;

/** DEMO: basket checkout selections (cleared for a fresh OPC visit). */
export const PRESIDIO_MOCK_BASKET_SELECTION_KEYS = [
  'presidio-mock-basket-shipping-addr',
  'presidio-mock-basket-invoice-addr',
  'presidio-mock-basket-shipping-method',
  'presidio-mock-basket-payment',
] as const;

/** DEMO: extra cart/checkout keys cleared on logout or user switch. */
const PRESIDIO_MOCK_CART_SESSION_EXTRA_KEYS = [
  'presidio-mock-basket-recurrence',
  'presidio-mock-basket-attributes',
  'presidio-mock-sfl-saved',
  'presidio-mock-sfl-lines',
] as const;

/**
 * DEMO: cart + checkout selections are global sessionStorage — clear when the
 * mock login changes so User B does not inherit User A's basket.
 */
export function resetPresidioMockCartSessionOnUserChange(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  clearPresidioMockBasketLines();
  for (const key of PRESIDIO_MOCK_BASKET_SELECTION_KEYS) {
    sessionStorage.removeItem(key);
  }
  for (const key of PRESIDIO_MOCK_CART_SESSION_EXTRA_KEYS) {
    sessionStorage.removeItem(key);
  }
  sessionStorage.removeItem(PRESIDIO_MOCK_USER_PREFS_KEY);
}

export function setPresidioMockActiveLogin(username: string | null | undefined): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  const previous = getPresidioMockActiveLogin();
  const login = (username || '').trim().toLowerCase();
  if (!login) {
    // Logout — drop cart so the next session does not inherit lines
    resetPresidioMockCartSessionOnUserChange();
    clearPresidioMockPunchoutSession();
    clearPresidioMockSalesRepSession();
    clearPresidioMockImpersonationSession();
    sessionStorage.removeItem(PRESIDIO_MOCK_ACTIVE_LOGIN_KEY);
    notifyPresidioFavoritesLoginChange();
    return;
  }
  // Different user (or first login after anonymous) — do not share cart across identities
  if (previous !== login) {
    resetPresidioMockCartSessionOnUserChange();
    clearPresidioMockSalesRepSession();
    clearPresidioMockImpersonationSession();
  }
  sessionStorage.setItem(PRESIDIO_MOCK_ACTIVE_LOGIN_KEY, login);
  if (isPresidioMockPunchoutLogin(login)) {
    seedPresidioMockPunchoutSession();
  } else {
    clearPresidioMockPunchoutSession();
  }
  if (previous !== login) {
    notifyPresidioFavoritesLoginChange();
  }
}

export function clearPresidioMockActiveLogin(): void {
  setPresidioMockActiveLogin(null);
}

export function getPresidioMockActiveLogin(): string {
  if (typeof sessionStorage === 'undefined') {
    return '';
  }
  return (sessionStorage.getItem(PRESIDIO_MOCK_ACTIVE_LOGIN_KEY) || '').trim().toLowerCase();
}

export function isPresidioMockWarehouseLogin(login?: string | null): boolean {
  const active = (login ?? getPresidioMockActiveLogin()).trim().toLowerCase();
  return active === PRESIDIO_MOCK_WAREHOUSE_LOGIN.toLowerCase();
}

export function isPresidioMockPunchoutLogin(login?: string | null): boolean {
  const active = (login ?? getPresidioMockActiveLogin()).trim().toLowerCase();
  return active === PRESIDIO_MOCK_PUNCHOUT_LOGIN.toLowerCase();
}

export function isPresidioMockSalesRepLogin(login?: string | null): boolean {
  const active = (login ?? getPresidioMockActiveLogin()).trim().toLowerCase();
  return active === PRESIDIO_MOCK_SALES_REP_LOGIN.toLowerCase();
}

export function isPresidioMockCsrLogin(login?: string | null): boolean {
  const active = (login ?? getPresidioMockActiveLogin()).trim().toLowerCase();
  return active === PRESIDIO_MOCK_CSR_LOGIN.toLowerCase();
}

export function clearPresidioMockSalesRepSession(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.removeItem(PRESIDIO_MOCK_ACTIVE_CUSTOMER_KEY);
  sessionStorage.removeItem(PRESIDIO_MOCK_SALES_ACCOUNT_PICKED_KEY);
  sessionStorage.removeItem(PRESIDIO_MOCK_SALES_ACCOUNT_PICKING_KEY);
}

export function clearPresidioMockImpersonationSession(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.removeItem(PRESIDIO_MOCK_IMPERSONATED_LOGIN_KEY);
  sessionStorage.removeItem(PRESIDIO_MOCK_IMPERSONATED_NAME_KEY);
}

export function getPresidioMockImpersonatedLogin(): string {
  if (typeof sessionStorage === 'undefined') {
    return '';
  }
  return (sessionStorage.getItem(PRESIDIO_MOCK_IMPERSONATED_LOGIN_KEY) || '').trim();
}

export function getPresidioMockImpersonatedDisplayName(): string {
  if (typeof sessionStorage === 'undefined') {
    return '';
  }
  return (sessionStorage.getItem(PRESIDIO_MOCK_IMPERSONATED_NAME_KEY) || '').trim();
}

export function isPresidioMockImpersonating(): boolean {
  return !!getPresidioMockImpersonatedLogin();
}

/** DEMO MVP 8.2.2: CSR starts viewing as an Acme end user. */
export function setPresidioMockImpersonatedLogin(
  login: string | null | undefined,
  displayName?: string | null
): boolean {
  if (typeof sessionStorage === 'undefined') {
    return false;
  }
  if (!isPresidioMockCsrLogin()) {
    return false;
  }
  const next = (login || '').trim();
  if (!next) {
    clearPresidioMockImpersonationSession();
    return true;
  }
  const catalog = getPresidioMockCsrSearchCatalog();
  const target = catalog.find(t => t.login.toLowerCase() === next.toLowerCase());
  if (!target) {
    return false;
  }
  sessionStorage.setItem(PRESIDIO_MOCK_IMPERSONATED_LOGIN_KEY, target.login);
  sessionStorage.setItem(
    PRESIDIO_MOCK_IMPERSONATED_NAME_KEY,
    (displayName || target.displayName || `${target.firstName} ${target.lastName}`).trim()
  );
  resetPresidioMockCartSessionOnUserChange();
  return true;
}

/**
 * DEMO: REST fixtures only exist for the core Acme targets; synthetic catalog
 * users fall back to Jordan's fixture while keeping the selected display name.
 */
export function getPresidioMockImpersonationFixtureLogin(): string {
  const login = getPresidioMockImpersonatedLogin();
  if (!login) {
    return '';
  }
  const real = PRESIDIO_MOCK_CSR_IMPERSONATION_TARGETS.find(t => t.login.toLowerCase() === login.toLowerCase());
  return real?.login || PRESIDIO_MOCK_DEFAULT_LOGIN;
}

export type PresidioMockCsrSearchUser = {
  login: string;
  username: string;
  firstName: string;
  lastName: string;
  displayName: string;
};

let csrSearchCatalogCache: PresidioMockCsrSearchUser[] | undefined;

/** Lazy DEMO catalog — real fixture users + synthetic rows for large-list search UX. */
export function getPresidioMockCsrSearchCatalog(): readonly PresidioMockCsrSearchUser[] {
  if (csrSearchCatalogCache) {
    return csrSearchCatalogCache;
  }
  const real: PresidioMockCsrSearchUser[] = PRESIDIO_MOCK_CSR_IMPERSONATION_TARGETS.map(t => ({
    login: t.login,
    username: t.username,
    firstName: t.firstName,
    lastName: t.lastName,
    displayName: t.displayName,
  }));
  const synthetic: PresidioMockCsrSearchUser[] = [];
  const firstNames = ['Casey', 'Dana', 'Ellis', 'Finn', 'Gray', 'Harper', 'Indigo', 'Jules', 'Kai', 'Logan'];
  const lastNames = ['Nguyen', 'Brooks', 'Singh', 'Ortiz', 'Keller', 'Walsh', 'Diaz', 'Frost', 'Quinn', 'Reed'];
  for (let i = 1; i <= PRESIDIO_MOCK_CSR_SEARCH_CATALOG_SIZE - real.length; i++) {
    const n = String(i).padStart(4, '0');
    const firstName = firstNames[i % firstNames.length];
    const lastName = lastNames[i % lastNames.length];
    const username = `acme.user${n}`;
    synthetic.push({
      login: `${username}@acme.com`,
      username,
      firstName,
      lastName,
      displayName: `${firstName} ${lastName}`,
    });
  }
  csrSearchCatalogCache = [...real, ...synthetic];
  return csrSearchCatalogCache;
}

/** Case-insensitive search by email (login) or username; caps result size for UI. */
export function searchPresidioMockCsrUsers(
  query: string,
  limit = 50
): PresidioMockCsrSearchUser[] {
  const q = (query || '').trim().toLowerCase();
  if (!q) {
    return [];
  }
  const catalog = getPresidioMockCsrSearchCatalog();
  const matches: PresidioMockCsrSearchUser[] = [];
  for (const user of catalog) {
    if (
      user.login.toLowerCase().includes(q) ||
      user.username.toLowerCase().includes(q) ||
      user.displayName.toLowerCase().includes(q)
    ) {
      matches.push(user);
      if (matches.length >= limit) {
        break;
      }
    }
  }
  return matches;
}

export function getPresidioMockActiveCustomerNo(): string {
  if (typeof sessionStorage === 'undefined') {
    return PRESIDIO_MOCK_DEFAULT_CUSTOMER_NO;
  }
  const stored = (sessionStorage.getItem(PRESIDIO_MOCK_ACTIVE_CUSTOMER_KEY) || '').trim();
  if (stored && PRESIDIO_MOCK_SALES_REP_ACCOUNTS.some(a => a.customerNo === stored)) {
    return stored;
  }
  return PRESIDIO_MOCK_DEFAULT_CUSTOMER_NO;
}

/** DEMO: company name for the active affiliate shopping context. */
export function getPresidioMockActiveCompanyName(): string {
  const customerNo = getPresidioMockActiveCustomerNo();
  return (
    PRESIDIO_MOCK_SALES_REP_ACCOUNTS.find(a => a.customerNo === customerNo)?.companyName || 'Acme Healthcare'
  );
}

/** DEMO: map affiliate company display name → customerNo (quote / pricing-request context). */
export function resolvePresidioMockCustomerNoByCompany(companyName?: string | null): string | undefined {
  const name = (companyName || '').trim().toLowerCase();
  if (!name) {
    return undefined;
  }
  return PRESIDIO_MOCK_SALES_REP_ACCOUNTS.find(a => a.companyName.toLowerCase() === name)?.customerNo;
}

/** DEMO: set active affiliate customer after login picker or header switch (MVP 8.2.1). */
export function setPresidioMockActiveCustomerNo(customerNo: string | null | undefined): boolean {
  if (typeof sessionStorage === 'undefined') {
    return false;
  }
  const next = (customerNo || '').trim();
  if (!PRESIDIO_MOCK_SALES_REP_ACCOUNTS.some(a => a.customerNo === next)) {
    return false;
  }
  const previous = getPresidioMockActiveCustomerNo();
  sessionStorage.setItem(PRESIDIO_MOCK_ACTIVE_CUSTOMER_KEY, next);
  sessionStorage.setItem(PRESIDIO_MOCK_SALES_ACCOUNT_PICKED_KEY, '1');
  if (previous !== next) {
    resetPresidioMockCartSessionOnUserChange();
  }
  return true;
}

export function hasPresidioMockSalesAccountPicked(): boolean {
  if (typeof sessionStorage === 'undefined') {
    return false;
  }
  return sessionStorage.getItem(PRESIDIO_MOCK_SALES_ACCOUNT_PICKED_KEY) === '1';
}

/** True while the login-gate Select account modal is open (or about to open). */
export function isPresidioMockSalesAccountPicking(): boolean {
  if (typeof sessionStorage === 'undefined') {
    return false;
  }
  return sessionStorage.getItem(PRESIDIO_MOCK_SALES_ACCOUNT_PICKING_KEY) === '1';
}

export function setPresidioMockSalesAccountPicking(picking: boolean): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  if (picking) {
    sessionStorage.setItem(PRESIDIO_MOCK_SALES_ACCOUNT_PICKING_KEY, '1');
  } else {
    sessionStorage.removeItem(PRESIDIO_MOCK_SALES_ACCOUNT_PICKING_KEY);
  }
}

/** DEMO: cXML punchout session keys expected by PunchoutService transfer. */
export function seedPresidioMockPunchoutSession(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem('punchout_SID', PRESIDIO_MOCK_PUNCHOUT_SID);
  sessionStorage.setItem('punchout_ReturnURL', PRESIDIO_MOCK_PUNCHOUT_RETURN_PATH);
  sessionStorage.setItem('punchout_BasketID', 'MOCK-BASKET-001');
}

export function clearPresidioMockPunchoutSession(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  for (const key of PRESIDIO_MOCK_PUNCHOUT_SESSION_KEYS) {
    sessionStorage.removeItem(key);
  }
}

export function isPresidioMockPunchoutSession(): boolean {
  if (typeof sessionStorage === 'undefined') {
    return false;
  }
  return isPresidioMockPunchoutLogin() && !!sessionStorage.getItem('punchout_SID');
}

/**
 * DEMO: map ICM current-customer shortcuts for multi-user mock logins.
 */
export function resolvePresidioMockCustomerRestPath(restPath: string): string {
  let path = restPath;
  try {
    path = decodeURIComponent(restPath);
  } catch {
    path = restPath;
  }

  if (isPresidioMockWarehouseLogin()) {
    return resolveWarehouseCustomerRestPath(path);
  }
  if (isPresidioMockPunchoutLogin()) {
    return resolvePunchoutCustomerRestPath(path);
  }
  if (isPresidioMockSalesRepLogin()) {
    return resolveSalesRepCustomerRestPath(path);
  }
  if (isPresidioMockCsrLogin()) {
    return resolveCsrCustomerRestPath(path);
  }
  // Acme SMB: rewrite customers/.../users/- to the active login fixture
  return resolveAcmeCustomerRestPath(path);
}

/**
 * DEMO MVP 8.2.2: CSR profile stays Quinn; when impersonating, user APIs resolve to the target.
 */
function resolveCsrCustomerRestPath(path: string): string {
  const cust = PRESIDIO_MOCK_DEFAULT_CUSTOMER_NO;
  const csrLogin = PRESIDIO_MOCK_CSR_LOGIN;
  const impersonated = getPresidioMockImpersonatedLogin();
  const fixtureUser = impersonated ? getPresidioMockImpersonationFixtureLogin() : csrLogin;
  const effectiveUser = fixtureUser;
  const actorOrDash = `(?:-|${escapeRegExp(csrLogin)}|${escapeRegExp(impersonated || csrLogin)}|${escapeRegExp(
    effectiveUser
  )})`;

  if (path === 'customers/-' || path === `customers/${cust}`) {
    return `customers/${cust}`;
  }

  const userPrefix = new RegExp(`^customers/(?:-|${cust})/users/${actorOrDash}(/.*)?$`, 'i');
  const userMatch = userPrefix.exec(path);
  if (userMatch) {
    const suffix = userMatch[1] || '';
    if (!suffix || suffix.startsWith('/roles') || suffix.startsWith('/budgets')) {
      return `customers/${cust}/users/${effectiveUser}${suffix}`;
    }
    if (
      /^\/(notifications|requisitions|quotes|quoterequests|recurringorders|costcenters)(\/|$)/i.test(suffix)
    ) {
      // Target user's portal data when impersonating; otherwise Jordan shared stubs for CSR alone
      const dataUser = impersonated ? getPresidioMockImpersonationFixtureLogin() : PRESIDIO_MOCK_DEFAULT_LOGIN;
      return `customers/${cust}/users/${dataUser}${suffix}`;
    }
    return `customers/-/users/-${suffix}`;
  }

  return path;
}

/**
 * DEMO MVP 8.2.1: sales rep views the selected affiliate customer; profile stays Riley.
 */
function resolveSalesRepCustomerRestPath(path: string): string {
  const cust = getPresidioMockActiveCustomerNo();
  const login = PRESIDIO_MOCK_SALES_REP_LOGIN;
  const knownCust = PRESIDIO_MOCK_SALES_REP_ACCOUNTS.map(a => escapeRegExp(a.customerNo)).join('|');
  const profileCust = PRESIDIO_MOCK_DEFAULT_CUSTOMER_NO;

  if (path === 'customers/-' || PRESIDIO_MOCK_SALES_REP_ACCOUNTS.some(a => path === `customers/${a.customerNo}`)) {
    return `customers/${cust}`;
  }

  // Address fixtures live under PRESIDIO-MOCK; catalog extras are served from memory.
  const addressMatch = new RegExp(`^customers/(?:-|${knownCust})/addresses(/.*)?$`, 'i').exec(path);
  if (addressMatch) {
    return `customers/${profileCust}/addresses${addressMatch[1] || ''}`;
  }

  const userPrefix = new RegExp(`^customers/(?:-|${knownCust})/users/(?:-|${escapeRegExp(login)})(/.*)?$`, 'i');
  const userMatch = userPrefix.exec(path);
  if (userMatch) {
    const suffix = userMatch[1] || '';
    if (!suffix || suffix.startsWith('/roles') || suffix.startsWith('/budgets')) {
      return `customers/${profileCust}/users/${login}${suffix}`;
    }
    if (
      /^\/(notifications|requisitions|quotes|quoterequests|recurringorders|costcenters)(\/|$)/i.test(suffix)
    ) {
      return `customers/${profileCust}/users/${PRESIDIO_MOCK_DEFAULT_LOGIN}${suffix}`;
    }
    return `customers/-/users/-${suffix}`;
  }

  return path;
}

/**
 * DEMO: known Acme logins that have dedicated user fixtures under PRESIDIO-MOCK.
 * Unknown logins keep the shared `users/-` Jordan stubs.
 */
export function isPresidioMockAcmeRoleLogin(login?: string | null): boolean {
  const active = (login ?? getPresidioMockActiveLogin()).trim().toLowerCase();
  if (!active) {
    return false;
  }
  return [
    PRESIDIO_MOCK_DEFAULT_LOGIN,
    PRESIDIO_MOCK_BUYER_LOGIN,
    PRESIDIO_MOCK_APPROVER_LOGIN,
    PRESIDIO_MOCK_ACCOUNT_OWNER_LOGIN,
    PRESIDIO_MOCK_NO_AFFILIATE_LOGIN,
  ]
    .map(l => l.toLowerCase())
    .includes(active);
}

/**
 * DEMO discovery: Acme users with affiliates share the sales-rep customer rewrite —
 * `customers/-` → selected affiliate; profile stays under PRESIDIO-MOCK.
 * Portal resources (devices/contracts/reports/workitems) remap to the home customer
 * in `rewriteAdminMyAccountCustomerPath` (interceptor) — Beta/Gamma folders have no
 * contracts/devices trees — then affiliate overlay stamps/merges assigned accounts (MVP 8.2.1).
 */
function resolveAcmeCustomerRestPath(path: string): string {
  const login = getPresidioMockActiveLogin();
  if (!login || !isPresidioMockAcmeRoleLogin(login)) {
    return path;
  }

  const activeCust = getPresidioMockActiveCustomerNo();
  const profileCust = PRESIDIO_MOCK_DEFAULT_CUSTOMER_NO;
  const knownCust = PRESIDIO_MOCK_SALES_REP_ACCOUNTS.map(a => escapeRegExp(a.customerNo)).join('|');

  if (path === 'customers/-' || PRESIDIO_MOCK_SALES_REP_ACCOUNTS.some(a => path === `customers/${a.customerNo}`)) {
    return `customers/${activeCust}`;
  }

  // Nested address APIs: keep under home customer fixtures (affiliate folders lack address trees).
  const addressMatch = new RegExp(`^customers/(?:-|${knownCust})/addresses(/.*)?$`, 'i').exec(path);
  if (addressMatch) {
    return `customers/${profileCust}/addresses${addressMatch[1] || ''}`;
  }

  const userPrefix = new RegExp(
    `^customers/(?:-|${knownCust})/users/(?:-|${escapeRegExp(login)})(/.*)?$`,
    'i'
  );

  const userMatch = userPrefix.exec(path);
  if (!userMatch) {
    return path;
  }

  const suffix = userMatch[1] || '';
  // Profile + roles + budgets are per-login fixtures (home customer, not active affiliate)
  if (!suffix || suffix.startsWith('/roles') || suffix.startsWith('/budgets')) {
    return `customers/${profileCust}/users/${canonicalAcmeLogin(login)}${suffix}`;
  }
  // Quotes / requisitions / etc. reuse Jordan shared demo data
  if (
    /^\/(notifications|requisitions|quotes|quoterequests|recurringorders|costcenters)(\/|$)/i.test(suffix)
  ) {
    return `customers/${profileCust}/users/${PRESIDIO_MOCK_DEFAULT_LOGIN}${suffix}`;
  }
  // Wishlists and other nested APIs use the shared `users/-` stubs
  return `customers/-/users/-${suffix}`;
}

function canonicalAcmeLogin(login: string): string {
  const lower = login.trim().toLowerCase();
  const known = [
    PRESIDIO_MOCK_DEFAULT_LOGIN,
    PRESIDIO_MOCK_BUYER_LOGIN,
    PRESIDIO_MOCK_APPROVER_LOGIN,
    PRESIDIO_MOCK_ACCOUNT_OWNER_LOGIN,
    PRESIDIO_MOCK_NO_AFFILIATE_LOGIN,
  ];
  return known.find(l => l.toLowerCase() === lower) || login.trim();
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function resolveWarehouseCustomerRestPath(path: string): string {
  const wh = PRESIDIO_MOCK_WAREHOUSE_CUSTOMER_NO;
  const login = PRESIDIO_MOCK_WAREHOUSE_LOGIN;
  const loginRe = login.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const userPrefix = new RegExp(`^customers/(?:-|${wh})/users/(?:-|${loginRe})(/.*)?$`, 'i');

  if (path === 'customers/-' || path === `customers/${wh}`) {
    return `customers/${wh}`;
  }

  const userMatch = userPrefix.exec(path);
  if (userMatch) {
    const suffix = userMatch[1] || '';
    if (suffix) {
      return resolveWarehouseNestedUserPath(suffix);
    }
    return `customers/${wh}/users/${login}`;
  }

  return path;
}

function resolvePunchoutCustomerRestPath(path: string): string {
  const cust = PRESIDIO_MOCK_DEFAULT_CUSTOMER_NO;
  const login = PRESIDIO_MOCK_PUNCHOUT_LOGIN;
  const loginRe = login.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const userPrefix = new RegExp(`^customers/(?:-|${cust})/users/(?:-|${loginRe})(/.*)?$`, 'i');

  const userMatch = userPrefix.exec(path);
  if (userMatch) {
    const suffix = userMatch[1] || '';
    // Roles / profile must be Pat; wishlists reuse shared -/users/- stubs
    if (suffix.startsWith('/roles') || !suffix) {
      return `customers/${cust}/users/${login}${suffix}`;
    }
    if (
      /^\/(budgets|notifications|requisitions|quotes|quoterequests|recurringorders|costcenters)(\/|$)/i.test(suffix)
    ) {
      return `customers/${cust}/users/${PRESIDIO_MOCK_DEFAULT_LOGIN}${suffix}`;
    }
    return `customers/-/users/-${suffix}`;
  }

  return path;
}

/**
 * Nested user APIs for warehouse demo: wishlists live under `customers/-/users/-`;
 * budgets/quotes/etc. reuse Acme jordan fixtures under PRESIDIO-MOCK.
 */
function resolveWarehouseNestedUserPath(suffix: string): string {
  const acmeNested =
    /^\/(budgets|roles|notifications|requisitions|quotes|quoterequests|recurringorders|costcenters)(\/|$)/i.test(
      suffix
    );
  if (acmeNested) {
    return `customers/${PRESIDIO_MOCK_DEFAULT_CUSTOMER_NO}/users/${PRESIDIO_MOCK_DEFAULT_LOGIN}${suffix}`;
  }
  return `customers/-/users/-${suffix}`;
}

export function isPresidioMockQuoteSubmitted(quoteRequestId: string): boolean {
  if (typeof sessionStorage === 'undefined') {
    return false;
  }
  return !!sessionStorage.getItem(`${PRESIDIO_MOCK_QR_SUBMITTED_PREFIX}${quoteRequestId}`);
}

/**
 * DEMO: preview checkout with / without My Account defaults.
 * `0` / `false` → no preferreds, empty basket selections.
 * `1` / `true` → restore fixture preferreds, empty basket selections.
 * Returns true when applied (caller should reload the page once).
 */
export function applyPresidioCheckoutDefaultsPreview(param: string | null | undefined): boolean {
  if (typeof sessionStorage === 'undefined' || param == null || param === '') {
    return false;
  }
  const normalized = param.trim().toLowerCase();
  const noDefaults = normalized === '0' || normalized === 'false' || normalized === 'off';
  const withDefaults = normalized === '1' || normalized === 'true' || normalized === 'on';
  if (!noDefaults && !withDefaults) {
    return false;
  }

  for (const key of PRESIDIO_MOCK_BASKET_SELECTION_KEYS) {
    sessionStorage.removeItem(key);
  }
  // Multi ship-to line assignments must not survive a defaults preview reset.
  clearPresidioMockBasketLineShipTos();
  if (noDefaults) {
    sessionStorage.setItem(PRESIDIO_MOCK_USER_PREFS_KEY, JSON.stringify({}));
  } else {
    sessionStorage.removeItem(PRESIDIO_MOCK_USER_PREFS_KEY);
  }
  return true;
}
